import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, CalendarPlus, Globe } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { eventTeamApiService } from '../../services/eventTeamApi';
import { calendarApiService } from '../../services/calendarApi';
import type { CalendarTag } from '../../types/calendar';
import type { EventItem, ScheduleEventResponse } from '../../types/eventTeam';
import { cn } from '../../utils/cn';

export interface ScheduleEventModalProps {
  isOpen: boolean;
  event: EventItem;
  onClose: () => void;
  onSuccess: (result: ScheduleEventResponse) => void;
}

function msToLocalInput(ms: number | null | undefined): string {
  if (ms == null) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToMs(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * One-click scheduling (guide §1.4, lead): creates + links a calendar event,
 * sets the run window and advances idea/approved → `scheduled`.
 */
export function ScheduleEventModal({ isOpen, event, onClose, onSuccess }: ScheduleEventModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [calendarTitle, setCalendarTitle] = useState('');
  const [calendarDescription, setCalendarDescription] = useState('');
  const [publish, setPublish] = useState(false);
  const [tags, setTags] = useState<CalendarTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStartTime(msToLocalInput(event.scheduled_start));
    setEndTime(msToLocalInput(event.scheduled_end));
    setCalendarTitle(event.title);
    setCalendarDescription(event.description || '');
    setPublish(false);
    setSelectedTagIds([]);
    setError(null);
    calendarApiService
      .getAllTags()
      .then(setTags)
      .catch(() => setTags([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const startMs = localInputToMs(startTime);
    if (startMs == null) {
      setError('A start time is required.');
      return;
    }
    const endMs = localInputToMs(endTime);
    if (endMs != null && endMs <= startMs) {
      setError('End time must be after the start time.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await eventTeamApiService.scheduleEvent(event.id, {
        start_time: startMs,
        end_time: endMs,
        calendar_title: calendarTitle.trim() || undefined,
        calendar_description: calendarDescription.trim() || undefined,
        tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
        publish,
      });
      onSuccess(result);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule event');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-xl"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-1 flex items-center gap-2">
          <CalendarPlus className="w-6 h-6 text-cyan-400" />
          Schedule event
        </h2>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Start time *</label>
              <input
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="glass-input w-full text-sm"
                required
              />
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">End time</label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="glass-input w-full text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Calendar title</label>
            <input
              type="text"
              value={calendarTitle}
              onChange={(e) => setCalendarTitle(e.target.value)}
              className="glass-input w-full text-sm"
              placeholder={event.title}
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Calendar description</label>
            <textarea
              value={calendarDescription}
              onChange={(e) => setCalendarDescription(e.target.value)}
              className="glass-input w-full text-sm"
              rows={3}
            />
          </div>

          {tags.length > 0 && (
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Calendar tags</label>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => {
                  const selected = selectedTagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() =>
                        setSelectedTagIds((prev) =>
                          selected ? prev.filter((id) => id !== tag.id) : [...prev, tag.id],
                        )
                      }
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border transition-all',
                        selected ? 'ring-1 ring-current' : 'opacity-60 hover:opacity-100',
                      )}
                      style={{
                        backgroundColor: `${tag.color}20`,
                        borderColor: `${tag.color}40`,
                        color: tag.color,
                      }}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-glass-300 cursor-pointer">
            <input
              type="checkbox"
              checked={publish}
              onChange={(e) => setPublish(e.target.checked)}
              className="rounded border-glass-200/30"
            />
            <Globe className="w-4 h-4 text-cyan-400" />
            Publish to the public ICS feed
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="glass-button-secondary text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !startTime}
              className="glass-button text-sm inline-flex items-center gap-1.5"
            >
              {submitting && <Loader className="w-4 h-4 animate-spin" />}
              Schedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
